import { ArpSegment } from "models/UserArpsModel";

import { CUBIC_FEET_TO_MCF } from "./arpsConstants";
import { getDurationInDays } from "./dates";
import { arpsWasm } from "./wasmLoader";

export interface RestrictedEURResult {
  restrictedEUR: number;
  qfAtCutoff: number;
}

/**
 * Calculate Restricted EUR for MULTIPLE SEGMENTS product
 *
 * This matches WASM's `restrict_secondary_products_end_dates` approach:
 * - WASM generates monthly forecast points (typically on day 1 of each month)
 * - WASM filters monthly points where point.day <= primary.day
 * - Monthly points represent cumulative production through the END of that month
 * - For a primary end date of Nov 22, WASM keeps points up through Nov 1
 * - The Nov 1 point's cumulative represents production through Nov 30
 *
 * Therefore, we calculate:
 * 1. EUR to END of month containing primary end date (e.g., Nov 30 for Nov 22 primary)
 * 2. qfAtCutoff as rate at start of that month (e.g., Nov 1 for Nov 22 primary)
 */
export function calculateRestrictedEURMultipleSegments(
  productSegments: ArpSegment[],
  primaryProductEndDate: Date
): RestrictedEURResult {
  const date = primaryProductEndDate.getUTCDate();

  // If the primary product end date is after the 15th of the month, use the current month
  // Otherwise, use the previous month for the cutoff calculation
  const monthOffset = date > 15 ? 1 : 0;

  // WASM's restrict_secondary_products_end_dates filters monthly points by calendar day.
  // Monthly forecast points represent cumulative production through the end of that month.
  // Calculate EUR through the end of the cutoff month determined by monthOffset above:
  // - If date > 15: use end of current month
  // - If date <= 15: use end of previous month
  const cutoffDate = new Date(
    Date.UTC(
      primaryProductEndDate.getUTCFullYear(),
      primaryProductEndDate.getUTCMonth() + monthOffset,
      0, // Day 0 = last day of target month
      23,
      59,
      59,
      999
    )
  );

  // For qfAtCutoff, use the start of the cutoff month
  const cutoffMonthStart = new Date(
    Date.UTC(
      primaryProductEndDate.getUTCFullYear(),
      primaryProductEndDate.getUTCMonth() + monthOffset,
      1,
      0,
      0,
      0,
      0
    )
  );

  const cutoffTimestamp = Math.round(cutoffDate.getTime() / 1000);

  let restrictedEUR = 0;
  let qfAtCutoff = 0;

  productSegments.forEach((segment) => {
    const segmentStartTimestamp = Math.round(
      new Date(segment.startDate).getTime() / 1000
    );
    const segmentEndTimestamp = Math.round(new Date(segment.endDate).getTime() / 1000);

    const isSegmentFullyBeforeCutoff = segmentEndTimestamp <= cutoffTimestamp;
    const isSegmentPartiallyBeforeCutoff =
      segmentStartTimestamp < cutoffTimestamp && segmentEndTimestamp > cutoffTimestamp;

    if (isSegmentFullyBeforeCutoff) {
      // Full segment is before cutoff - include entire segment volume
      const segVolume =
        arpsWasm.getSegmentVolume(
          segment.qi,
          segment.di,
          segment.b,
          segment.qf,
          BigInt(segmentStartTimestamp),
          BigInt(segmentEndTimestamp)
        ) * CUBIC_FEET_TO_MCF;
      restrictedEUR += segVolume;
    } else if (isSegmentPartiallyBeforeCutoff) {
      // Segment spans the cutoff - use calculateSegmentCumulativeVolumeOverDays
      const segVolume =
        arpsWasm.calculateSegmentCumulativeVolumeOverDays(
          segment,
          BigInt(segmentStartTimestamp),
          BigInt(cutoffTimestamp)
        ) * CUBIC_FEET_TO_MCF;
      restrictedEUR += segVolume;

      // Calculate the flow rate at the START of cutoff month (matching WASM's monthly point)
      const timeElapsedToMonthStart = getDurationInDays(
        segment.startDate,
        cutoffMonthStart.toISOString()
      );
      qfAtCutoff = arpsWasm.getRateAtDay(
        segment.qi,
        segment.di,
        segment.b,
        timeElapsedToMonthStart
      );
    }
  });

  return {
    restrictedEUR,
    qfAtCutoff
  };
}
